import { Minus, Plus, User, Users, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { HomeLanguagePicker } from './HomeLanguagePicker'
import { ChatFileUpload } from '@/components/chat/ChatFileUpload'
import { uploadHostedChatVideo } from '@/components/chat/stimulus-videos'
import { ChatImage } from '@/lib/api'
import { CHAT_CREDITS_PER_MSG, ROOM_CREDITS_PER_MSG } from '@/lib/billing/credit-rates'
import { CreditIcon } from '@/components/icons/CreditIcon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

type HomeChatType = 'interview' | 'group'

const MIN_GROUP_PARTICIPANTS = 2
const MAX_GROUP_PARTICIPANTS = 10

interface HomeComposerOptionsBarProps {
  chatType: HomeChatType
  onChatTypeChange: (type: HomeChatType) => void
  participantCount: number
  onParticipantCountChange: (count: number) => void
  language: string
  onLanguageChange: (code: string) => void
  images: Array<ChatImage>
  onImagesChange: (images: Array<ChatImage>) => void
  /** Merge-safe emit for the video hosting window (pass the React state setter, see
   *  useChatFileIngest.onImagesMerge). Only meaningful with `acceptVideo`. */
  onImagesMerge?: (updater: (prev: Array<ChatImage>) => Array<ChatImage>) => void
  pdfText: string | null
  pdfName: string | null
  onPdfChange: (text: string | null, name: string | null) => void
  disabled?: boolean
  /** Audience tier of the currently selected twin — used to show credit rate. */
  audienceTier?: string
  /** Effective rates from the audience payload. Used ahead of the tier map. */
  chatCreditsPerMessage?: number
  focusGroupCreditsPerMessage?: number
  /**
   * Accept video creatives (MP4/WebM/MOV, hosted on ingest via uploadHostedChatVideo).
   * OFF by default on purpose: the home flow hands pending files to the new room through
   * a sessionStorage payload (HomeStage's 'home-pending-files'), and a raw video's base64
   * would blow the ~5MB sessionStorage quota and break Start Chat. The parent may opt in
   * once its handoff carries hosted refs (and forwards them as `stimulusVideos` per the
   * wire contract) instead of raw bytes.
   */
  acceptVideo?: boolean
}

export function HomeComposerOptionsBar({
  chatType,
  onChatTypeChange,
  participantCount,
  onParticipantCountChange,
  language,
  onLanguageChange,
  images,
  onImagesChange,
  onImagesMerge,
  pdfText,
  pdfName,
  onPdfChange,
  disabled = false,
  audienceTier,
  chatCreditsPerMessage,
  focusGroupCreditsPerMessage,
  acceptVideo = false,
}: HomeComposerOptionsBarProps) {
  const tierRate = audienceTier
    ? (chatType === 'group' ? ROOM_CREDITS_PER_MSG : CHAT_CREDITS_PER_MSG)[audienceTier] ?? null
    : null
  const quotedRate = chatType === 'group' ? focusGroupCreditsPerMessage : chatCreditsPerMessage
  const creditRate = quotedRate ?? tierRate

  return (
    <div className="flex items-center justify-between gap-2 w-full">
      <div className="flex items-center gap-2">
        <ChatTypePill
          value={chatType}
          onChange={onChatTypeChange}
          disabled={disabled}
        />
        {chatType === 'group' && (
          <ParticipantStepper
            count={participantCount}
            onChange={onParticipantCountChange}
            disabled={disabled}
          />
        )}
        <HomeLanguagePicker
          value={language}
          onChange={onLanguageChange}
          disabled={disabled}
        />
        <ChatFileUpload
          images={images}
          onImagesChange={onImagesChange}
          onImagesMerge={onImagesMerge}
          pdfText={pdfText}
          pdfName={pdfName}
          onPdfChange={onPdfChange}
          disabled={disabled}
          compact
          acceptVideo={acceptVideo}
          uploadHosted={acceptVideo ? uploadHostedChatVideo : undefined}
        />
      </div>

      {creditRate !== null && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-xs font-medium select-none',
                  'text-[var(--home-credit-rate-fg)]',
                )}
              >
                <CreditIcon className="h-3 w-3" />
                {creditRate} / message
                <Info className="h-2.5 w-2.5 ml-0.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs max-w-[160px] text-center">
              Credits charged per message you send
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  )
}

interface ParticipantStepperProps {
  count: number
  onChange: (count: number) => void
  disabled?: boolean
}

function ParticipantStepper({ count, onChange, disabled }: ParticipantStepperProps) {
  const clamped = Math.min(MAX_GROUP_PARTICIPANTS, Math.max(MIN_GROUP_PARTICIPANTS, count))
  const canDecrement = !disabled && clamped > MIN_GROUP_PARTICIPANTS
  const canIncrement = !disabled && clamped < MAX_GROUP_PARTICIPANTS

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-full p-0.5',
        'bg-[var(--home-stepper-bg)]',
      )}
      data-testid="home-participant-stepper"
    >
      <button
        type="button"
        disabled={!canDecrement}
        onClick={() => onChange(clamped - 1)}
        data-testid="home-participant-decrement"
        aria-label="Decrease participants"
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-full transition-all duration-150',
          'text-[var(--home-stepper-fg)] hover:text-[var(--home-stepper-fg-hover)]',
          !canDecrement && 'pointer-events-none opacity-40',
        )}
      >
        <Minus className="h-3 w-3" strokeWidth={2.4} />
      </button>
      <span
        className={cn(
          'min-w-[24px] px-1 text-center text-xs font-medium tabular-nums',
          'text-[hsl(var(--home-stepper-count-fg))]',
        )}
        data-testid="home-participant-count"
      >
        {clamped}
      </span>
      <button
        type="button"
        disabled={!canIncrement}
        onClick={() => onChange(clamped + 1)}
        data-testid="home-participant-increment"
        aria-label="Increase participants"
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-full transition-all duration-150',
          'text-[var(--home-stepper-fg)] hover:text-[var(--home-stepper-fg-hover)]',
          !canIncrement && 'pointer-events-none opacity-40',
        )}
      >
        <Plus className="h-3 w-3" strokeWidth={2.4} />
      </button>
    </div>
  )
}

interface ChatTypePillProps {
  value: HomeChatType
  onChange: (value: HomeChatType) => void
  disabled?: boolean
}

function ChatTypePill({ value, onChange, disabled }: ChatTypePillProps) {
  const options: Array<{ key: HomeChatType; label: string; icon: typeof User }> = [
    { key: 'interview', label: 'Interview', icon: User },
    { key: 'group', label: 'Focus Group', icon: Users },
  ]

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-full p-0.5',
        'bg-[var(--home-pill-track-bg)]',
      )}
      data-testid="home-chat-type-toggle"
    >
      {options.map(({ key, label, icon: Icon }) => {
        const isActive = value === key
        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            onClick={() => onChange(key)}
            data-testid={`home-chat-type-${key}`}
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-all duration-150',
              isActive
                ? 'bg-[var(--home-pill-active-bg)] text-[hsl(var(--home-pill-active-fg))] shadow-sm'
                : 'text-[var(--home-pill-inactive-fg)] hover:text-[var(--home-pill-inactive-fg-hover)]',
              disabled && 'pointer-events-none opacity-50',
            )}
          >
            <Icon className="h-3 w-3" strokeWidth={2} />
            {label}
          </button>
        )
      })}
    </div>
  )
}

export type { HomeChatType }
